import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  signal,
} from '@angular/core';
import { BdExportAsFileAsyncData } from '@bindoc-projects/util-domain-lib';
import {
  BD_EXPORT_AS_FILE_ASYNC_CONFIG_DEFAULT,
  BdExportAsFileAsyncConfig,
} from './export-config';
import {
  BdExportAsFileAsyncDialogComponent,
  BdExportAsFileAsyncDialogData,
  BdExportAsFileAsyncDialogResult,
} from './dialog/dialog.component';
import { BdCurrentUserService } from '@bindoc-projects/user-lib/data';
import {
  BdDataTestDirective,
  DIALOG_CONFIG_FOR_FILTERS,
} from '@bindoc-projects/util-lib/ui';
import { Button } from 'primeng/button';
import { Tooltip } from 'primeng/tooltip';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { DialogService, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BD_DIALOG_SERVICE_PROVIDER } from '@bindoc-projects/ui-lib/dialog/ui';

@Component({
  selector: 'bd-export-as-file-async',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <p-toast position="bottom-center" />
    <p-button
      icon="pi pi-download"
      severity="secondary"
      [rounded]="true"
      [text]="true"
      [data-test]="config().trigger.dataTestKey"
      [pTooltip]="config().trigger.tooltipText"
      [ariaLabel]="config().trigger.tooltipText"
      (onClick)="onClick()"
    />
  `,
  imports: [BdDataTestDirective, Button, Tooltip, ToastModule],
  providers: [BD_DIALOG_SERVICE_PROVIDER, MessageService],
})
export class BdExportAsFileAsyncComponent {
  private readonly _dialogService = inject(DialogService);
  private readonly _messageService = inject(MessageService);
  private readonly _currentUserService = inject(BdCurrentUserService);
  private readonly _destroyRef = inject(DestroyRef);

  public readonly data = input<BdExportAsFileAsyncData>();
  public readonly filename = input<string>();
  public readonly config = input<BdExportAsFileAsyncConfig>(
    BD_EXPORT_AS_FILE_ASYNC_CONFIG_DEFAULT,
  );

  public readonly showTrigger = signal(false);

  constructor() {
    this._currentUserService.authenticated$
      .pipe(takeUntilDestroyed())
      .subscribe((authenticated) => {
        this.showTrigger.set(authenticated);
      });
  }

  public onClick(): void {
    const dialogConfig: DynamicDialogConfig<BdExportAsFileAsyncDialogData> = {
      ...DIALOG_CONFIG_FOR_FILTERS,
      header: 'Export',
      data: {
        data: this.data(),
        filename: this.filename(),
        config: this.config(),
      },
    };

    this._dialogService
      .open(BdExportAsFileAsyncDialogComponent, dialogConfig)
      .onClose.pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((result?: BdExportAsFileAsyncDialogResult) => {
        if (!result) {
          return;
        }
        this._notify(result.status);
      });
  }

  /** Terminal feedback for the export request — a toast replaces the former in-dialog success/error screen. */
  private _notify(status: 'ok' | 'error'): void {
    if (status === 'ok') {
      this._messageService.add({
        severity: 'success',
        summary: 'Export gestartet',
        detail:
          'Der Export wird im Hintergrund erstellt. Sie werden per E-Mail benachrichtigt, sobald er bereit ist.',
      });
    } else {
      this._messageService.add({
        severity: 'error',
        summary: 'Export fehlgeschlagen',
        detail:
          'Der Export konnte nicht gestartet werden. Bitte versuchen Sie es später erneut.',
      });
    }
  }
}
